import { useLocation, useNavigate } from 'react-router-dom';
import { logout, type SessionClaims } from '../api.js';
import { canSubmitAnalysis } from '../roles.js';
import { isSection } from '../nav.js';
import { Brand } from './Brand.js';
import { McpTokenPanel } from './McpTokenPanel.js';

// Both application headers need it, so it lives in `nav.ts`. Re-exported here because that is where
// it was, and `navigation.test.tsx` asserts it against this module.
export { isSection } from '../nav.js';

/** Pull a human label (email) from the permissive claims, falling back gracefully. */
function identityLabel(claims: SessionClaims): string | null {
  const email = claims['email'];
  if (typeof email === 'string') return email;
  const sub = claims['sub'];
  return typeof sub === 'string' ? sub : null;
}

/** The authed app header: logo (links home) + signed-in identity + sign out. */
/**
 * Whether to offer the Access section at all.
 *
 * ⚠️ A COURTESY, NOT A CONTROL. `GET /admin/members` 403s a member or viewer on its own, and RLS sits
 * beneath that — hiding the link stops someone bumping into a page that will refuse them, and nothing
 * more. Anyone can still type the URL, and the page handles that by SAYING so rather than rendering
 * an empty table.
 *
 * An unknown role hides the link: the claims are permissively typed, so a missing `role` arrives as
 * undefined, and "I do not know what you are" resolves to "do not offer", never "probably fine".
 */
export function canSeeAccess(claims: SessionClaims): boolean {
  const role = claims['role'];
  return role === 'owner' || role === 'admin';
}

export function AppHeader({ claims }: { claims: SessionClaims }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isCurrent = (path: string): boolean => isSection(pathname, path);
  const label = identityLabel(claims);

  async function onSignOut() {
    await logout();
    void navigate('/sign-in', { replace: true });
  }

  return (
    <header className="app__header">
      {/*
        THE MARK AND THE APPLICATION'S NAME, as one affordance — the same lockup the Hub carries.

        The Hub gained its wordmark when it stopped wearing this header, and that left the analyzer as
        the only one of the two apps that could not say what it was: a staff member who uses both saw
        the GGPathOS mark on both origins and had the tab title to tell them apart. One brand, two
        names, said on every page (Brad, 2026-08-26 — "everything should be GGP Branding but these are
        two separate apps").
      */}
      <button
        type="button"
        className="app__brandlink app__brandlink--named"
        onClick={() => void navigate('/')}
        aria-label="Deal Analyzer — GGPathOS home"
      >
        <Brand />
        {/* `aria-hidden`: the button's own label already says it, and a screen reader should not hear
            the product's name twice on every page. */}
        <span className="app__wordmark" aria-hidden="true">
          Deal Analyzer
        </span>
      </button>
      <div className="app__identity">
        {/* "You are here". Without it the header is four identical links and the only way to know
            which section you are in is to remember what you clicked. `aria-current` carries the same
            fact to a screen reader, where a colour change carries nothing. */}
        {/* Deals comes FIRST because it is the one people come back for. Until it existed a finished
            underwrite was reachable only by the id the app had just handed you — navigate away and the
            report was gone, with no history anywhere in the product. */}
        <button
          type="button"
          className={`app__navlink${isCurrent('/analyses') ? ' app__navlink--on' : ''}`}
          {...(isCurrent('/analyses') ? { 'aria-current': 'page' as const } : {})}
          onClick={() => void navigate('/analyses')}
        >
          Deals
        </button>
        <button
          type="button"
          className={`app__navlink${isCurrent('/projects') ? ' app__navlink--on' : ''}`}
          {...(isCurrent('/projects') ? { 'aria-current': 'page' as const } : {})}
          onClick={() => void navigate('/projects')}
        >
          Projects
        </button>
        <button
          type="button"
          className={`app__navlink${isCurrent('/standards') ? ' app__navlink--on' : ''}`}
          {...(isCurrent('/standards') ? { 'aria-current': 'page' as const } : {})}
          onClick={() => void navigate('/standards')}
        >
          Scoring standard
        </button>
        {/*
          🔴 ADDED BECAUSE THE PAGE HAD NO DOOR. `/admin/members` shipped with a route and no link, so
          it was reachable only by typing the URL — built, deployed, and unreachable, which is the
          same failure as not building it. Placed last of the sections because administering access is
          something you go and do, not somewhere you work.
        */}
        {canSeeAccess(claims) && (
          <button
            type="button"
            className={`app__navlink${isCurrent('/admin/members') ? ' app__navlink--on' : ''}`}
            {...(isCurrent('/admin/members') ? { 'aria-current': 'page' as const } : {})}
            onClick={() => void navigate('/admin/members')}
          >
            Access
          </button>
        )}
        <McpTokenPanel />
        {label && <span className="app__email">{label}</span>}
        {/*
          The PRIMARY ACTION, and it was missing entirely.

          The header listed four SECTIONS and no way to start a deal. Home is the deal-entry page, and
          the only route to it was the logo — an unlabelled affordance that people do not read as
          "new deal". `AnalysisView` had its own "New analysis" button, so the action existed on
          exactly one page of five; from Projects, a project, or Deals there was no way forward at all.

          Styled as a BUTTON, not a nav link, because it is a verb rather than a destination — and it
          sits outside the section group so it never lights up as "you are here".
        */}
        {/*
          ⚠️ AND IT IS ON EVERY PAGE, which is what makes it the one to miss. A grep for the write
          CALLS does not find it — it navigates, it does not submit — so it survived the first count
          of viewer-visible write actions and was caught only by mounting the pages as a viewer.

          For a read-only client this advertised deal creation on every screen in the product, and
          (once `/` redirects them) delivered them back to the list they were already on: a primary
          action, in the header, that goes nowhere. Not a 403 — just a promise the product cannot keep.
        */}
        {canSubmitAnalysis(claims) && (
          <button type="button" className="app__newdeal" onClick={() => void navigate('/')}>
            + New deal
          </button>
        )}
        <button type="button" className="app__signout" onClick={() => void onSignOut()}>
          Sign out
        </button>
      </div>
    </header>
  );
}
